import { RouteConfig } from "vue-router";
import i18n from "@/i18n";

import PlaceHolder from "@/views/PlaceHolder.vue";
import TokenReceiver from "@/views/TokenReceiver.vue";
import Logout from "@/views/Logout.vue";
import Login from "@/views/Login.vue";
import Forbidden from "@/views/Forbidden.vue";
import { CorePermission, SubsidiaryIds } from "@iloq/shared";
import ExtranetLicenses from "@/views/ExtranetLicenses.vue";
import Frontpage from "@/views/Frontpage.vue";
import Returns from "@/views/Returns.vue";
import ReturnsContact from "@/views/ReturnsContact.vue";
import NewReturn from "@/views/NewReturn.vue";
import MaintenanceReturn from "@/views/MaintenanceReturn.vue";
import Support from "@/views/Support.vue";
import Profile from "@/views/Profile.vue";
import Brandstore from "@/views/Brandstore.vue";
import ProductDataCard from "@/views/ProductDataCard.vue";
import ListBulletin from "@/views/ListBulletin.vue";
import Bulletin from "@/views/Bulletin.vue";
import OrderContainer from "@/views/OrderContainer.vue";
import ListOrders from "@/views/ListOrders.vue";
import ViewOrder from "@/views/ViewOrder.vue";
import Academy from "@/views/Academy.vue";
import SalesReturn from "@/views/SalesReturn.vue";
import CampaignReturn from "@/views/CampaignReturn.vue";
import OldReturn from "@/views/OldReturn.vue";
import ProductSearch from "@/views/ProductSearch.vue";
import PartnerFailure from "@/views/PartnerFailure.vue";
import Events from "@/views/Events.vue";
import Material from "@/views/Material.vue";
import ReLogin from "@/views/ReLogin.vue";
import Solutions from "@/views/Solutions.vue";
import ViewSolution from "@/views/ViewSolution.vue";
import ViewSolutionListing from "@/views/ViewSolutionListing.vue";
import SolutionListContainer from "@/views/SolutionListContainer.vue";
import SolutionSearch from "@/views/SolutionSearch.vue";
import SupportRequestList from "@/views/SupportRequestList.vue";
import SupportRequestChat from "@/views/SupportRequestChat.vue";
import CreateSupportRequest from "@/views/CreateSupportRequest.vue";
import GlobalSearch from "@/views/GlobalSearch.vue";
import PartnerBlocked from "@/views/PartnerBlocked.vue";
import SubscriptionIframe from "@/views/SubscriptionIframe.vue";
import config from "@/config";

const getPartnerProgramReactConnector = () => import("@/views/PartnerProgramReactConnector.vue");
const getPartnerInfoManagementReactConnector = () =>
  import("@/views/PartnerInfoManagementReactConnector.vue");
const getUserManagementReactConnector = () => import("@/views/UserManagementReactConnector.vue");

export const routes: RouteConfig[] = [
  {
    // tokens route receives authToken, refreshToken and original azureAdToken JWTs in url
    path: "/tokens/:authToken/:refreshToken/:azureAdToken",
    name: "tokens",
    props: true,
    component: TokenReceiver,
    meta: {
      loginLayout: true,
      hidden: true,
    },
  },
  {
    path: "/logout",
    name: "logout",
    component: Logout,
  },
  {
    path: "/re-login",
    name: "re-login",
    component: ReLogin,
  },
  {
    path: "/login",
    name: "login",
    component: Login,
  },
  {
    path: "/partner-failure",
    name: "partner_failure",
    component: PartnerFailure,
  },
  {
    path: "/partner-blocked",
    name: "partner_blocked",
    component: PartnerBlocked,
  },
  {
    path: "/",
    name: "home",
    component: Frontpage,
    props: { default: true, page: "Front page" },
    meta: { authRequired: true, permissionsRequired: [CorePermission.login] },
  },
  {
    path: "/profile",
    name: "profile",
    component: Profile,
    props: { default: true, page: "User Profile" },
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login],
    },
  },
  {
    path: "/products/:sku",
    name: "productdatacard",
    component: ProductDataCard,
    props: true,
    meta: { authRequired: true, permissionsRequired: [CorePermission.login] },
  },
  {
    path: "/orders",
    name: "orders",
    component: ListOrders,
    props: { default: true, page: "Orders" },
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login, CorePermission.order],
    },
  },
  {
    path: "/ordering/licenses",
    name: "licenses",
    component: ExtranetLicenses,
    props: { default: true, page: "Licenses (embedded from Extranet)" },
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login, CorePermission.licenses],
    },
  },
  {
    path: "/subscriptions/new",
    name: "new_subscription",
    component: SubscriptionIframe,
    props: { default: true, page: "New License (embedded from SaaB client)" },
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login, CorePermission.licenses],
      landingPage: "/new-subscription",
      onlyAllowedForSpecificCountries: {
        countries: config.get("subscriptionViewAllowedCountryList"),
        redirectRouteName: "licenses",
      },
    },
  },
  {
    path: "/subscriptions/list",
    name: "subscriptions_list",
    component: SubscriptionIframe,
    props: { default: true, page: "Multifamily licenses (embedded from SaaB client)" },
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login, CorePermission.licenses],
      landingPage: "/subscriptions",
      onlyAllowedForSpecificCountries: {
        countries: config.get("subscriptionViewAllowedCountryList"),
        redirectRouteName: "licenses",
      },
    },
  },
  {
    path: "/marketing_material",
    name: "marketing_material",
    component: Material,
    props: { default: true, page: "Marketing Material" },
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login],
    },
  },
  {
    path: "/learning",
    name: "learning",
    component: Academy,
    props: { default: true, page: "Learning" },
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login, CorePermission.academy],
    },
  },
  {
    path: "/support",
    name: "support_frontpage",
    component: SolutionListContainer,
    props: { default: true, page: "Support", topLevel: true },
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login],
    },
  },
  {
    path: "/support/solutions/categories",
    name: "solution_categories",
    component: SolutionListContainer,
    props: true,
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login],
      breadCrumb: {
        name: "solutions-root",
        href: "/support/solutions",
      },
    },
    children: [
      {
        path: ":categoryIdStr",
        name: "solution_folders_of_category",
        component: ViewSolutionListing,
        props: true,
        meta: {
          authRequired: true,
          permissionsRequired: [CorePermission.login],
          breadCrumb: {
            name: ":categoryIdStr",
            href: "/support/solutions/categories/:categoryIdStr",
          },
        },
      },
      {
        path: ":categoryIdStr/:folderIdStr",
        name: "solution_articles_of_folder",
        component: ViewSolutionListing,
        props: true,
        meta: {
          authRequired: true,
          permissionsRequired: [CorePermission.login],
          breadCrumb: [
            {
              name: ":categoryIdStr",
              href: "/support/solutions/categories/:categoryIdStr",
            },
            {
              name: ":folderIdStr",
              href: "/support/solutions/categories/:categoryIdStr/:folderIdStr",
            },
          ],
        },
        children: [
          {
            path: ":solutionId",
            name: "solution_details",
            component: ViewSolution,
            props: true,
            meta: { authRequired: true, permissionsRequired: [CorePermission.login] },
          },
        ],
      },
    ],
  },
  {
    path: "/support/solutions",
    name: "solutions",
    component: Solutions,
    props: { default: true, page: "Solutions" },
    meta: { authRequired: true, permissionsRequired: [CorePermission.login] },
  },

  {
    path: "/support/solutions/search",
    name: "solution_search",
    component: SolutionSearch,
    props: true,
    meta: { authRequired: true, permissionsRequired: [CorePermission.login] },
  },
  {
    path: "/support/solutions/list",
    name: "solution_articles_list_container",
    component: SolutionListContainer,
    props: true,
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login],
      breadCrumb: {
        name: i18n.t("solutions.article.breadcrumb.root").toString(),
        href: "/support/solutions",
      },
    },
    children: [
      {
        path: "/support/solutions/list/:listName",
        name: "solution_articles_list",
        component: ViewSolutionListing,
        props: true,
        meta: { authRequired: true, permissionsRequired: [CorePermission.login] },
      },
    ],
  },
  {
    path: "/support/returns",
    name: "returns",
    component: Returns,
    props: { default: true, page: "Returns" },
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login, CorePermission.returns],
      disabledFor:
        config.get("disableReturnsForInc") === true
          ? [
              {
                id: SubsidiaryIds.USA,
                redirectRouteName: "returns_contact",
              },
            ]
          : undefined,
    },
  },
  {
    path: "/support/returns/contact",
    name: "returns_contact",
    component: ReturnsContact,
    props: { default: true, page: "Returns Contact" },
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login, CorePermission.returns],
      disabledFor:
        config.get("disableReturnsForInc") === true
          ? [
              {
                id: SubsidiaryIds.FINLAND,
                redirectRouteName: "returns",
              },
            ]
          : [
              {
                id: SubsidiaryIds.FINLAND,
                redirectRouteName: "returns",
              },
              {
                id: SubsidiaryIds.USA,
                redirectRouteName: "returns",
              },
            ],
    },
  },
  {
    path: "/support/returns/new",
    name: "new_return",
    component: NewReturn,
    props: { default: true, page: "New Return" },
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login, CorePermission.returns],
      disabledFor:
        config.get("disableReturnsForInc") === true
          ? [
              {
                id: SubsidiaryIds.USA,
                redirectRouteName: "returns_contact",
              },
            ]
          : undefined,
    },
  },
  {
    path: "/support/returns/maintenance_return/:rmaId",
    name: "maintenance_return_details",
    component: MaintenanceReturn,
    props: true,
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login, CorePermission.returns],
      disabledFor:
        config.get("disableReturnsForInc") === true
          ? [
              {
                id: SubsidiaryIds.USA,
                redirectRouteName: "returns_contact",
              },
            ]
          : undefined,
    },
  },
  {
    path: "/support/returns/sales_return/:rmaId",
    name: "sales_return_details",
    component: SalesReturn,
    props: true,
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login, CorePermission.returns],
      disabledFor:
        config.get("disableReturnsForInc") === true
          ? [
              {
                id: SubsidiaryIds.USA,
                redirectRouteName: "returns_contact",
              },
            ]
          : undefined,
    },
  },
  {
    path: "/support/returns/campaign_return/:rmaId",
    name: "campaign_return_details",
    component: CampaignReturn,
    props: true,
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login, CorePermission.returns],
      disabledFor:
        config.get("disableReturnsForInc") === true
          ? [
              {
                id: SubsidiaryIds.USA,
                redirectRouteName: "returns_contact",
              },
            ]
          : undefined,
    },
  },
  {
    path: "/support/returns/old_return/:rmaId",
    name: "old_return_details",
    component: OldReturn,
    props: true,
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login, CorePermission.returns],
      disabledFor:
        config.get("disableReturnsForInc") === true
          ? [
              {
                id: SubsidiaryIds.USA,
                redirectRouteName: "returns_contact",
              },
            ]
          : undefined,
    },
  },
  {
    path: "/support/instructions",
    name: "support_instructions",
    component: Support,
    props: { default: true, page: "How to Create Support Ticket" },
    meta: { authRequired: true, permissionsRequired: [CorePermission.login] },
  },
  {
    path: "/support/requests",
    name: "support_requests",
    component: SupportRequestList,
    props: true,
    meta: { authRequired: true, permissionsRequired: [CorePermission.login] },
  },
  {
    path: "/support/requests/create",
    name: "support_request_create",
    component: CreateSupportRequest,
    props: true,
    meta: { authRequired: true, permissionsRequired: [CorePermission.login] },
  },
  {
    path: "/support/requests/:id",
    name: "support_request_details",
    component: SupportRequestChat,
    props: true,
    meta: { authRequired: true, permissionsRequired: [CorePermission.login] },
  },
  {
    path: "/brandstore",
    name: "brandstore",
    component: Brandstore,
    props: { default: true, page: "iLOQ Brand Store" },
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login, CorePermission.brandstore],
    },
  },
  {
    path: "/bulletin",
    name: "list_bulletin",
    component: ListBulletin,
    props: { default: true, page: "Bulletin" },
    meta: { authRequired: true, permissionsRequired: [CorePermission.login] },
  },
  {
    path: "/bulletin/:id",
    name: "bulletin",
    component: Bulletin,
    props: (route) => ({
      ...route.params,
      ...{ id: Number.parseInt(route.params.id, 10) || undefined },
    }),
    meta: { authRequired: true, permissionsRequired: [CorePermission.login] },
  },
  {
    path: "/events",
    name: "events",
    component: Events,
    props: { default: true, page: "Events" },
    meta: { authRequired: true, permissionsRequired: [CorePermission.login] },
  },
  {
    path: "/shopping_cart",
    name: "shopping_cart",
    component: OrderContainer,
    props: true,
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login, CorePermission.order],
    },
  },
  {
    path: "/orders/:id",
    name: "view_order",
    component: ViewOrder,
    props: true,
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login, CorePermission.order],
    },
  },
  {
    path: "/products",
    name: "products",
    component: ProductSearch,
    props: true,
    meta: { authRequired: true, permissionsRequired: [CorePermission.login] },
  },
  {
    // User doesn't have rights to a route and is redirected here
    path: "/forbidden",
    name: "forbidden",
    component: Forbidden,
    meta: { authRequired: true },
  },
  {
    // Page for testing missing permission
    path: "/secret",
    name: "secret",
    component: PlaceHolder,
    props: { default: true, page: "Secret page" },
    meta: { authRequired: true, permissionsRequired: ["secretNoPermission"] },
  },
  {
    path: "/partner_program",
    name: "partner_program",
    props: { default: true, page: "Partner Program" },
    component: () => getPartnerProgramReactConnector(),
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login, CorePermission.partnerProgram],
      rootRoute: "/partner_program",
    },
  },
  {
    path: "/partner_program/*",
    name: "partner_program_nested",
    props: { default: true, page: "Partner Program" },
    component: () => getPartnerProgramReactConnector(),
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login, CorePermission.partnerProgram],
      rootRoute: "/partner_program",
    },
  },
  {
    path: "/partner_info_management",
    name: "partner_info_management",
    props: { default: true, page: "Partner Info Management" },
    component: () => getPartnerInfoManagementReactConnector(),
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login],
      rootRoute: "/partner_info_management",
    },
  },
  {
    path: "/partner_user_management",
    name: "partner_user_management",
    props: { default: true, page: "User Management" },
    component: () => getUserManagementReactConnector(),
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login, CorePermission.viewUserManagement],
      rootRoute: "/partner_user_management",
    },
  },
  {
    path: "/partner_user_management/*",
    name: "partner_user_management_nested",
    props: { default: true, page: "User Management" },
    component: () => getUserManagementReactConnector(),
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login, CorePermission.viewUserManagement],
      rootRoute: "/partner_user_management",
    },
  },
  {
    path: "/search",
    name: "global_search",
    component: GlobalSearch,
    props: { default: true, page: "Search" },
    meta: {
      authRequired: true,
      permissionsRequired: [CorePermission.login],
    },
  },
];
